import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { Socket } from "socket.io-client";
import { Gamepad2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TournamentBracket } from "@/components/TournamentBracket";
import { TournamentMatchupWheel } from "@/components/TournamentMatchupWheel";
import {
  api,
  ApiError,
  compareGamesByName,
  isCustomAppId,
  type CommonGame,
  type Me,
  type RoomDetails,
  type TournamentObjectiveCatalogEntry,
  type TournamentState,
} from "@/lib/api";
import { getMatchById, tournamentChampionSteamId } from "@/lib/tournament-utils";

interface TournamentSectionProps {
  code: string;
  socket: Socket;
  me: Me;
  details: RoomDetails;
  /** Room wheel disabled set (same as library wheel). */
  disabled: Set<number>;
  /** Disable objective / exclusion edits during any spin. */
  editsLocked?: boolean;
  onTournamentSpinningChange?: (spinning: boolean) => void;
}

function normalizeTournament(t: TournamentState): TournamentState {
  return {
    ...t,
    objectiveCatalog: t.objectiveCatalog ?? [],
    tournamentWheelExclusions: t.tournamentWheelExclusions ?? [],
  };
}

export function TournamentSection({
  code,
  socket,
  me,
  details,
  disabled,
  editsLocked = false,
  onTournamentSpinningChange,
}: TournamentSectionProps) {
  const [tournament, setTournament] = useState<TournamentState | null>(null);
  const [loading, setLoading] = useState(true);
  const [objText, setObjText] = useState("");
  const [objGameKey, setObjGameKey] = useState<string>("");

  const refreshTournament = useCallback(async () => {
    try {
      const t = await api.getTournament(code);
      setTournament(normalizeTournament(t));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      toast.error(err instanceof Error ? err.message : "Could not load tournament");
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    void refreshTournament();
  }, [refreshTournament]);

  useEffect(() => {
    function onState(s: TournamentState) {
      setTournament((prev) =>
        normalizeTournament({
          ...s,
          currentMutualGames: s.currentMutualGames ?? prev?.currentMutualGames,
          objectiveCatalog: s.objectiveCatalog ?? prev?.objectiveCatalog ?? [],
          tournamentWheelExclusions:
            s.tournamentWheelExclusions ?? prev?.tournamentWheelExclusions ?? [],
        }),
      );
      if (
        s.status === "active" &&
        (s.phase === "pick_game" || s.phase === "awaiting_result")
      ) {
        void refreshTournament();
      }
    }
    socket.on("tournament:state", onState);
    return () => {
      socket.off("tournament:state", onState);
    };
  }, [socket, refreshTournament]);

  useEffect(() => {
    function onConfig(payload: {
      objectiveCatalog: TournamentObjectiveCatalogEntry[];
      tournamentWheelExclusions: number[];
    }) {
      setTournament((prev) =>
        prev
          ? {
              ...prev,
              objectiveCatalog: payload.objectiveCatalog,
              tournamentWheelExclusions: payload.tournamentWheelExclusions,
            }
          : null,
      );
    }
    socket.on("tournament:config", onConfig);
    return () => {
      socket.off("tournament:config", onConfig);
    };
  }, [socket]);

  useEffect(() => {
    function onWheelToggled() {
      void refreshTournament();
    }
    socket.on("wheel:toggled", onWheelToggled);
    return () => {
      socket.off("wheel:toggled", onWheelToggled);
    };
  }, [socket, refreshTournament]);

  const isHost = details.ownerSteamId === me.steamId;
  const syncedCount = details.members.filter((m) => m.lastLibrarySyncAt).length;
  const syncedMembers = details.members.filter((m) => m.lastLibrarySyncAt);
  const meSyncedInRoom = details.members.some(
    (m) => m.steamId === me.steamId && m.lastLibrarySyncAt,
  );

  const canStartSolo =
    meSyncedInRoom &&
    (syncedMembers.length === 1
      ? syncedMembers.some((m) => m.steamId === me.steamId)
      : isHost);

  async function handleStart() {
    try {
      await api.startTournament(code);
      await refreshTournament();
      toast.success("Tournament started");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start tournament");
    }
  }

  async function handleStartSolo() {
    try {
      await api.startSoloTournament(code);
      await refreshTournament();
      toast.success("Solo challenge started");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start solo challenge");
    }
  }

  function handleReset() {
    socket.emit("tournament:reset", { code });
  }

  function handleSetWinner(winnerSteamId: string) {
    if (!tournament?.currentMatchId) return;
    socket.emit("tournament:setWinner", {
      code,
      matchNodeId: tournament.currentMatchId,
      winnerSteamId,
    });
  }

  function handleSoloNext() {
    socket.emit("tournament:soloNext", { code });
  }

  async function handleAddObjective() {
    const text = objText.trim();
    if (!text) {
      toast.error("Enter objective text");
      return;
    }
    const appId = objGameKey === "" ? null : Number(objGameKey);
    if (objGameKey !== "" && (!Number.isInteger(appId) || appId === 0)) {
      toast.error("Pick a valid game or “All games”");
      return;
    }
    try {
      await api.addTournamentObjective(code, { text, appId });
      setObjText("");
      await refreshTournament();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not add objective");
    }
  }

  async function handleDeleteObjective(id: string) {
    try {
      await api.deleteTournamentObjective(code, id);
      await refreshTournament();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not remove objective");
    }
  }

  async function handleToggleTournamentExclusion(appId: number, excluded: boolean) {
    try {
      await api.setTournamentWheelExclusion(code, { appId, excluded });
      await refreshTournament();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update exclusion");
    }
  }

  const sortedRoomGames = useMemo(
    () => [...details.commonGames].sort(compareGamesByName),
    [details.commonGames],
  );

  const mutualGames: CommonGame[] = useMemo(() => {
    if (!tournament) return [];
    return (tournament.currentMutualGames ?? []).map((g) => ({
      ...g,
      custom: g.custom ?? isCustomAppId(g.appId),
    }));
  }, [tournament]);

  const wheelGames = useMemo(
    () => mutualGames.filter((g) => !disabled.has(g.appId)),
    [mutualGames, disabled],
  );

  const exclSet = useMemo(
    () => new Set(tournament?.tournamentWheelExclusions ?? []),
    [tournament?.tournamentWheelExclusions],
  );

  if (loading || !tournament) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tournament</CardTitle>
          <CardDescription>Loading…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const fmt = tournament.format ?? "bracket";
  const currentMatch =
    tournament.currentMatchId != null
      ? getMatchById(tournament.nodes, tournament.currentMatchId)
      : undefined;

  const selectedGameMeta =
    tournament.selectedAppId != null
      ? mutualGames.find((g) => g.appId === tournament.selectedAppId) ??
        details.commonGames.find((g) => g.appId === tournament.selectedAppId) ??
        null
      : null;

  const selectedGameLabel =
    selectedGameMeta?.name ??
    (tournament.selectedAppId != null ? `Steam app ${tournament.selectedAppId}` : null);

  const soloPlayerSteamId = fmt === "solo" ? tournament.seeds[0] : null;
  const canSpinTournamentWheel =
    fmt === "solo"
      ? soloPlayerSteamId === me.steamId && tournament.status === "active"
      : isHost && tournament.status === "active";

  const canResetTournament =
    tournament.status === "active" &&
    (isHost || (fmt === "solo" && soloPlayerSteamId === me.steamId));

  const catalog = tournament.objectiveCatalog ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {isHost && tournament.status !== "active" && (
          <Button onClick={handleStart} disabled={syncedCount < 2}>
            Start bracket tournament
          </Button>
        )}
        {tournament.status !== "active" && (
          <Button variant="secondary" onClick={handleStartSolo} disabled={!canStartSolo}>
            Solo challenge
          </Button>
        )}
        {canResetTournament && (
          <Button variant="outline" onClick={handleReset}>
            {fmt === "solo" ? "End challenge" : "Reset tournament"}
          </Button>
        )}
        {syncedCount < 2 && (
          <span className="text-sm text-muted-foreground">
            Bracket mode needs two synced libraries. Solo only needs yours (see rules below).
          </span>
        )}
        {syncedMembers.length >= 2 && !isHost && (
          <span className="text-sm text-muted-foreground">
            With multiple synced players, only the room host can start a solo challenge here.
          </span>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Objectives pool</CardTitle>
          <CardDescription>
            Anyone in the room can add objectives. Use <strong>All games</strong> for a general
            challenge, or tie one to a specific title. After each spin, one eligible objective is
            chosen at random (or none if nothing matches).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {catalog.length > 0 && (
            <ul className="space-y-2 text-sm">
              {catalog.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border/50 bg-muted/20 px-3 py-2"
                >
                  <div>
                    <p className="font-medium">{row.text}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.appId == null
                        ? "Applies to any game"
                        : `Game: ${details.commonGames.find((g) => g.appId === row.appId)?.name ?? `App ${row.appId}`}`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-destructive hover:text-destructive"
                    disabled={editsLocked}
                    onClick={() => void handleDeleteObjective(row.id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="obj-text">
                New objective
              </label>
              <Input
                id="obj-text"
                value={objText}
                disabled={editsLocked}
                onChange={(e) => setObjText(e.target.value)}
                placeholder="e.g. Finish on hardest difficulty"
                maxLength={500}
              />
            </div>
            <div className="w-full space-y-1 sm:w-52">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="obj-game">
                Scope
              </label>
              <select
                id="obj-game"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={objGameKey}
                disabled={editsLocked}
                onChange={(e) => setObjGameKey(e.target.value)}
              >
                <option value="">All games</option>
                {sortedRoomGames.map((g) => (
                  <option key={g.appId} value={String(g.appId)}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <Button type="button" disabled={editsLocked} onClick={() => void handleAddObjective()}>
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tournament wheel exclusions</CardTitle>
          <CardDescription>
            Extra games removed from the <em>tournament</em> matchup wheel only (in addition to
            games turned off in the room list). Everyone in the room can change this.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
            {sortedRoomGames.map((g) => {
              const checked = exclSet.has(g.appId);
              return (
                <li key={g.appId} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`texcl-${g.appId}`}
                    className="size-4 rounded border-input"
                    checked={checked}
                    disabled={editsLocked}
                    onChange={(e) =>
                      void handleToggleTournamentExclusion(g.appId, e.target.checked)
                    }
                  />
                  <label htmlFor={`texcl-${g.appId}`} className="cursor-pointer select-none">
                    {g.name}
                  </label>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {tournament.status === "idle" && (
        <Card>
          <CardHeader>
            <CardTitle>No active challenge</CardTitle>
            <CardDescription>
              <strong>Bracket tournament</strong> — single elimination. For each matchup the host
              spins the shared library (respecting disabled games and tournament exclusions above),
              then players compete on the objective drawn from the pool. The host declares the
              winner to advance the bracket.
            </CardDescription>
            <CardDescription className="pt-2">
              <strong>Solo challenge</strong> — one player spins their mutual room library for a
              game and a random matching objective from the pool, then can spin again anytime. If
              you are the only synced member you can start solo; with several synced members only
              the host can start a solo run for themselves.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {tournament.status === "active" && fmt === "bracket" && (
        <>
          <TournamentBracket nodes={tournament.nodes} members={details.members} />

          {tournament.phase === "pick_game" && (
            <TournamentMatchupWheel
              games={wheelGames}
              socket={socket}
              roomCode={code}
              matchNodeId={tournament.currentMatchId}
              meSteamId={me.steamId}
              members={details.members}
              canSpin={canSpinTournamentWheel}
              phase={tournament.phase}
              onSpinningChange={onTournamentSpinningChange}
            />
          )}

          {tournament.phase === "awaiting_result" && currentMatch && (
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="text-base">This matchup</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  {selectedGameMeta?.iconUrl ? (
                    <img
                      src={selectedGameMeta.iconUrl}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30">
                      <Gamepad2 className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium uppercase text-muted-foreground">Game</p>
                    <p className="text-lg font-semibold leading-tight">
                      {selectedGameLabel ?? "—"}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground">Objective</p>
                  {tournament.objective ? (
                    <p className="text-base font-medium">{tournament.objective.text}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No objective in the pool matched this game. Add global or game-specific
                      entries above.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-border/50 pt-4">
                  {isHost ? (
                    <>
                      <Button
                        onClick={() =>
                          currentMatch.playerA && handleSetWinner(currentMatch.playerA)
                        }
                        disabled={!currentMatch.playerA}
                      >
                        {details.members.find((m) => m.steamId === currentMatch.playerA)
                          ?.displayName ?? "A"}{" "}
                        wins
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          currentMatch.playerB && handleSetWinner(currentMatch.playerB)
                        }
                        disabled={!currentMatch.playerB}
                      >
                        {details.members.find((m) => m.steamId === currentMatch.playerB)
                          ?.displayName ?? "B"}{" "}
                        wins
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Waiting for host to declare a winner.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {tournament.status === "active" && fmt === "solo" && (
        <>
          {tournament.phase === "pick_game" && (
            <TournamentMatchupWheel
              games={wheelGames}
              socket={socket}
              roomCode={code}
              matchNodeId={tournament.currentMatchId}
              meSteamId={me.steamId}
              members={details.members}
              canSpin={canSpinTournamentWheel}
              phase={tournament.phase}
              wheelTitle="Your games"
              spinButtonHint={
                canSpinTournamentWheel
                  ? "Spin for a game & objective"
                  : soloPlayerSteamId !== me.steamId
                    ? "Only the person in the solo challenge can spin"
                    : undefined
              }
              onSpinningChange={onTournamentSpinningChange}
            />
          )}

          {tournament.phase === "awaiting_result" && (
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="text-base">Your challenge</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  {selectedGameMeta?.iconUrl ? (
                    <img
                      src={selectedGameMeta.iconUrl}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30">
                      <Gamepad2 className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium uppercase text-muted-foreground">Game</p>
                    <p className="text-lg font-semibold leading-tight">
                      {selectedGameLabel ?? "—"}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground">Objective</p>
                  {tournament.objective ? (
                    <p className="text-base font-medium">{tournament.objective.text}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No objective in the pool matched this game.
                    </p>
                  )}
                </div>
                <div className="border-t border-border/50 pt-4">
                  {soloPlayerSteamId === me.steamId ? (
                    <Button onClick={handleSoloNext}>Spin again</Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Waiting for {details.members.find((m) => m.steamId === soloPlayerSteamId)
                        ?.displayName ?? "player"}{" "}
                      to spin again or end the challenge.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {tournament.status === "complete" && fmt === "bracket" && (
        <>
          <TournamentBracket nodes={tournament.nodes} members={details.members} />
          <Card className="border-primary/40 bg-primary/5">
            <CardHeader>
              <CardTitle>Tournament complete</CardTitle>
              <CardDescription>
                Champion:{" "}
                <strong>
                  {(() => {
                    const champ = tournamentChampionSteamId(tournament.nodes);
                    return champ
                      ? details.members.find((m) => m.steamId === champ)?.displayName ?? champ
                      : "—";
                  })()}
                </strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isHost && (
                <Button variant="outline" onClick={handleReset}>
                  Clear tournament
                </Button>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
